import { useState, useEffect } from "react";
import {
  addPetFood,
  getPetFoods,
  deletePetFood,
} from "../../utils/back4AppApi";
import { KinshipPetProfile, BrandedFood } from "../../utils/kinshipTypes";
import {
  Table,
  TableBody,
  Grid,
  TableRow,
  TableCell,
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  Stack,
  TableContainer,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { translate } from "../../utils/localisation";

interface Props {
  pet?: KinshipPetProfile;
  onAction?: Function;
}

export function Foods({ pet, onAction = () => {} }: Props) {
  const [foodSearch, setFoodSearch] = useState("");
  const [newFoodId, setNewFoodId] = useState("");
  const [foodSuggestions, setFoodSuggestions] = useState([]);
  useEffect(() => {
    const delayDebounceFn = setTimeout(async () => {
      const res = await getPetFoods(`?query=${foodSearch}`);
      setFoodSuggestions(res.data.foods);
      if (res.data.foods.length > 0) {
        if (res.data.foods[0].productName === undefined) {
          setNewFoodId(res.data.foods[0].petFood.id);
        } else {
          setNewFoodId(res.data.foods[0].id);
        }
      }
    }, 1000);

    return () => clearTimeout(delayDebounceFn);
  }, [foodSearch]);
  const onFoodAction = async (event: any, ...args: Array<string | null>) => {
    event.preventDefault();
    const actionType = args[0];
    const actionArg = args[1];
    const actionHasConfirmDialogStep = args[2];
    if (actionHasConfirmDialogStep === "NEEDS_CONFIRMATION") {
      // eslint-disable-next-line no-restricted-globals
      if (confirm(translate("are_you_sure_delete_food"))) {
        onFoodAction(event, actionType, actionArg);
      }
      return;
    }
    if (actionType === "ADD") {
      const data = {
        branded: [
          {
            brandedFoodId: newFoodId,
          },
        ],
      };
      const result = await addPetFood(pet?.kinshipPetId, data);
      onAction(result);
    } else if (actionType === "DELETE") {
      const result = await deletePetFood(pet?.kinshipPetId, newFoodId);
      onAction(result);
    }
  };
  return (
    <Box sx={{ mt: 2, p: 4, border: 1, borderColor: "gray", borderRadius: 5 }}>
      <Grid container>
        <Grid item xs={6}>
          <Typography
            variant="h4"
            component="h4"
            sx={{ mb: 2, fontWeight: "medium" }}
          >
            {translate("foods")}
          </Typography>
          <TableContainer>
            <Table>
              <TableBody>
                {pet?.petFoods?.branded?.map(
                  (food: BrandedFood, index: number) => {
                    return (
                      <TableRow key={index}>
                        <TableCell align="left" colSpan={2}>
                          {food?.productName}
                        </TableCell>
                        <TableCell align="left" colSpan={2}>
                          <Stack direction="row" spacing={1}>
                            <IconButton
                              color="primary"
                              aria-label={translate("delete")}
                              onClick={event =>
                                onFoodAction(event, "DELETE", food?.productName)
                              }
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  }
                )}
                <Table
                  size="small"
                  sx={{
                    tableLayout: { xs: "auto", sm: "fixed" }, // keeps columns sane
                    width: "100%",
                    "& .MuiTableCell-root": { p: { xs: 0.75, sm: 1 } },
                  }}
                >
                  {/* ...your TableBody rows... */}

                  {/* ADD ROW (fixed) */}
                  <TableRow>
                    {/* Inputs cell */}
                    <TableCell
                      align="left"
                      colSpan={2}
                      sx={{
                        width: "100%",
                        minWidth: 0, // allow shrinking
                      }}
                    >
                      <Stack
                        component="div"
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        sx={{
                          width: "100%",
                          flexWrap: "wrap",
                          "& > *": { minWidth: { xs: "100%", sm: 200 } },
                        }}
                      >
                        <TextField
                          fullWidth
                          id="newName"
                          name="newName"
                          label={translate("add_food")}
                          variant="outlined"
                          size="small"
                          value={foodSearch}
                          onChange={e => setFoodSearch(e.target.value)}
                        />

                        {/* Select wrapped in FormControl so label doesn't overflow */}
                        <FormControl fullWidth size="small">
                          <InputLabel id="food-select-label">
                            {translate("select")}
                          </InputLabel>
                          <Select
                            labelId="food-select-label"
                            label={translate("select")}
                            value={newFoodId}
                            displayEmpty
                            onChange={e =>
                              setNewFoodId(e.target.value as string)
                            }
                            MenuProps={{
                              // keep menu reasonable and inside viewport; avoid pushing layout
                              PaperProps: {
                                style: { maxHeight: 320, maxWidth: 360 },
                              },
                              disablePortal: true,
                            }}
                            sx={{
                              // prevent selected value from forcing wide cell
                              "& .MuiSelect-select": {
                                display: "block",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              },
                            }}
                            renderValue={val => {
                              if (!val) return translate("select");
                              const f = foodSuggestions.find(
                                (x: any) =>
                                  (x.productName === undefined
                                    ? x.petFood?.id
                                    : x.id) === val
                              ) as any;
                              const text =
                                f?.productName ?? f?.petFood?.productName ?? "";
                              return (
                                <span
                                  style={{
                                    display: "block",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {text || translate("select")}
                                </span>
                              );
                            }}
                          >
                            {foodSuggestions.map((food: any, index: number) => {
                              const value =
                                food.productName === undefined
                                  ? food.petFood?.id
                                  : food.id;
                              const label =
                                food.productName ??
                                food.petFood?.productName ??
                                "";
                              return (
                                <MenuItem
                                  key={value || index}
                                  value={value}
                                  sx={{
                                    whiteSpace: "normal",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {label}
                                </MenuItem>
                              );
                            })}
                          </Select>
                        </FormControl>
                      </Stack>
                    </TableCell>

                    {/* Action cell */}
                    <TableCell
                      align="left"
                      colSpan={2}
                      sx={{
                        whiteSpace: "nowrap",
                        width: { xs: "auto", sm: 1 },
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                        justifyContent="flex-end"
                      >
                        <Button
                          type="submit"
                          variant="contained"
                          onClick={event => onFoodAction(event, "ADD")}
                        >
                          {translate("add_food")}
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                </Table>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </Box>
  );
}
